import React, { useState, useEffect, useCallback, createContext } from 'react';

import { useAxios } from '../hooks/useAxios';
import { ILogoutData, IUserAppSettings, IUserContext } from '../models/userContext.model';

const UserContext = createContext<IUserContext>({
  isLoggedIn: false,
  userId: null,
  userName: null,
  email: null,
  isAdmin: false,
  userSettings: null,
  token: null,
  expirationDate: null,
  logoutData: null,
  login: (userId: string, userName: string, email: string, isAdmin: boolean, userSettings: IUserAppSettings, token: string, expirationDate?: Date) => {},
  logout: (logoutData: ILogoutData | null) => {},
  setLogoutData: (logoutData: ILogoutData | null) => {},
  toggleDarkMode: (isDarkMode: boolean) => {}
});
let logoutTimerId: number;
interface IProps {
  children: JSX.Element;
};

export const UserContextProvider: React.FC<IProps> = props => {
  const [userId, setUserId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userSettings, setUserSettings] = useState<IUserAppSettings | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [logoutData, setLogoutData] = useState<ILogoutData | null>(null);
  const { sendRequest } = useAxios();

  const loginHandler = useCallback((userId: string, userName: string, email: string, isAdmin: boolean, userSettings: IUserAppSettings,
    token: string, expirationDate?: Date) => {
    setUserId(userId);
    setUserName(userName);
    setEmail(email);
    setIsAdmin(isAdmin);
    setUserSettings(userSettings || null);
    setToken(token);
    const tokenExpirationDate = expirationDate ?? new Date(new Date().getTime() + 1000 * 3600 * 24);
    setExpirationDate(tokenExpirationDate);
    setLogoutData(null);
    localStorage.setItem(
      'pegasusUserInfo',
      JSON.stringify({
        userId,
        userName,
        email,
        isAdmin,
        userSettings,
        token,
        expirationDate: tokenExpirationDate.toISOString()
      }));
  }, []);
  
  const logoutHandler = useCallback((logoutData: ILogoutData | null) => {
    setUserId(null);
    setUserName(null);
    setEmail(null);
    setIsAdmin(false);
    setUserSettings(null);
    setToken(null);
    setExpirationDate(null);
    setLogoutData(logoutData);
    localStorage.removeItem('pegasusUserInfo');
  }, []);

  const changeLogoutDataHandler = useCallback((logoutData: ILogoutData | null) => {
    setLogoutData(logoutData);
  }, []);

  const toggleDarkModeHandler = useCallback(async (isDarkMode: boolean) => {
    setUserSettings(settings => {
      const updatedSettings = settings ? {...settings} : {};
      if (updatedSettings) {
        updatedSettings.isDarkMode = isDarkMode;
      }
      return updatedSettings;
    });
    const storedInfoString = localStorage.getItem('pegasusUserInfo');
    const storedInfo = {
      ...storedInfoString ? JSON.parse(storedInfoString) : null
    };
    if (!storedInfo.userSettings) {
      storedInfo.userSettings = {};
    }
    storedInfo.userSettings.isDarkMode = isDarkMode;
    localStorage.setItem('pegasusUserInfo', JSON.stringify(storedInfo));
    if (token) {
      try {
        const callResponse = await sendRequest(
          '/users/darkMode',
          'PATCH',
          {
            isDarkMode
          },
          { Authorization: `Bearer ${token}` }
        );
        return callResponse;
      } catch (error) { }
    }
  }, [token, sendRequest]);

  useEffect(() => {
    const storedInfoString = localStorage.getItem('pegasusUserInfo');
    const storedInfo = {
      ...storedInfoString ? JSON.parse(storedInfoString) : null
    };
    let actualDate = new Date();
    actualDate.setHours(actualDate.getHours() + 3);
    if (storedInfo && storedInfo.token && new Date(storedInfo.expirationDate) > actualDate) {
      loginHandler(storedInfo.userId, storedInfo.userName, storedInfo.email, storedInfo.isAdmin, storedInfo.userSettings, storedInfo.token,
        new Date(storedInfo.expirationDate));
    }
  }, [loginHandler]);

  useEffect(() => {
    if (token && expirationDate) {
      const timeToLive = expirationDate.getTime() - new Date().getTime();
      logoutTimerId = setTimeout(logoutHandler, timeToLive);
    }
    else {
      clearTimeout(logoutTimerId);
    }
  }, [token, expirationDate, logoutHandler]);

  return (
    <UserContext.Provider
      value = {{
        isLoggedIn: !!token,
        userId,
        userName,
        email,
        isAdmin,
        userSettings,
        token,
        expirationDate,
        logoutData,
        login: loginHandler,
        logout: logoutHandler,
        setLogoutData: changeLogoutDataHandler,
        toggleDarkMode: toggleDarkModeHandler
      }}>
        {props.children}
    </UserContext.Provider>
  );
};

export default UserContext;